<script setup>
import { computed } from 'vue';
import Icon from 'next/icon/Icon.vue';
import { useI18n } from 'vue-i18n';
import { useMessageContext } from './provider.js';
import { hasOneDayPassed } from 'shared/helpers/timeHelper';
import { ORIENTATION, MESSAGE_STATUS } from './constants';

// CRM Monotributo patch 0012: explain documented Meta delivery error codes in
// plain Spanish next to the raw error. Only codes documented by Meta are
// mapped — any other code (or an error without a code) keeps showing the raw
// string alone, as before.
const WHATSAPP_DELIVERY_ERROR_HINTS = {
  131026:
    'El número no puede recibir WhatsApp: puede no existir, no tener cuenta de WhatsApp, tener la app muy desactualizada o no haber aceptado los términos. Verificá el número antes de reintentar.',
  131047:
    'Pasaron más de 24 horas desde el último mensaje del cliente: hay que reenganchar con una plantilla.',
  131048:
    'Envíos pausados por límite de spam del número. Esperá y reintentá más tarde.',
  131049:
    'Meta decidió no entregar este mensaje (límites de marketing por usuario). Reintentá más tarde.',
  131056:
    'Demasiados mensajes a este mismo número en poco tiempo. Esperá unos minutos y reintentá.',
};

const RETRY_UNDELIVERABLE_CONFIRM =
  'Meta reportó que este número no puede recibir WhatsApp. Es probable que vuelva a fallar. ¿Reintentar igual?';

const props = defineProps({
  error: { type: String, required: true },
});

const emit = defineEmits(['retry']);

const { orientation, status, createdAt, content, attachments } =
  useMessageContext();

const { t } = useI18n();

const canRetry = computed(() => {
  const hasContent = content.value !== null;
  const hasAttachments = attachments.value && attachments.value.length > 0;
  return !hasOneDayPassed(createdAt.value) && (hasContent || hasAttachments);
});

const errorCode = computed(() => props.error?.match(/^(\d+)/)?.[1] ?? null);

const errorHint = computed(() =>
  errorCode.value ? WHATSAPP_DELIVERY_ERROR_HINTS[errorCode.value] || '' : ''
);

const onRetryClick = () => {
  if (
    errorCode.value === '131026' &&
    !window.confirm(RETRY_UNDELIVERABLE_CONFIRM)
  ) {
    return;
  }
  emit('retry');
};
</script>

<template>
  <div class="text-xs text-n-ruby-11 flex items-center gap-1.5">
    <span>{{ t('CHAT_LIST.FAILED_TO_SEND') }}</span>
    <div class="relative group">
      <div
        class="bg-n-alpha-2 rounded-md size-5 grid place-content-center cursor-pointer"
      >
        <Icon
          icon="i-lucide-alert-triangle"
          class="text-n-ruby-11 size-[14px]"
        />
      </div>
      <div
        class="absolute bg-n-alpha-3 px-4 py-3 border rounded-xl border-n-strong text-n-slate-12 bottom-6 w-52 text-xs backdrop-blur-[100px] shadow-[0px_0px_24px_0px_rgba(0,0,0,0.12)] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all break-all"
        :class="{
          'ltr:left-0 rtl:right-0': orientation === ORIENTATION.LEFT,
          'ltr:right-0 rtl:left-0': orientation === ORIENTATION.RIGHT,
        }"
      >
        {{ error }}
        <span v-if="errorHint" class="block mt-1 text-n-slate-11">
          {{ errorHint }}
        </span>
      </div>
    </div>
    <button
      v-if="canRetry"
      type="button"
      :disabled="status !== MESSAGE_STATUS.FAILED"
      class="bg-n-alpha-2 rounded-md size-5 grid place-content-center cursor-pointer"
      @click="onRetryClick"
    >
      <Icon icon="i-lucide-refresh-ccw" class="text-n-ruby-11 size-[14px]" />
    </button>
  </div>
</template>
